import { useState } from "react";
import { Link } from "react-router-dom";
import { Scissors, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        toast.success("Welcome back");
      } else {
        await register(email.trim(), password, name.trim() || undefined);
        toast.success("Account created");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isLogin = mode === "login";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="auth-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
            <Scissors className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight text-zinc-900 leading-none">
              BreakItDown
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mt-1">
              Break it down. Get it done.
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
            {isLogin ? "Welcome back" : "Create your account"}
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none text-zinc-900 mb-8">
            {isLogin ? "Sign in." : "Get started."}
          </h1>

          <button
            type="button"
            onClick={googleLogin}
            className="w-full h-12 flex items-center justify-center gap-3 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 text-sm font-medium text-zinc-900 transition-colors mb-4"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.3 29.3 35.5 24 35.5 17.6 35.5 12.5 30.4 12.5 24S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.3 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.3 29.2 4.5 24 4.5 16.3 4.5 9.6 9 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43.5c5.1 0 9.8-1.7 13.4-4.7l-6.2-5.1c-2 1.5-4.5 2.4-7.2 2.4-5.3 0-9.6-3.2-11.3-7.5l-6.5 5C9.3 39 16.1 43.5 24 43.5z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.1C40.9 35.4 43.5 30.1 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              or {isLogin ? "sign in" : "sign up"} with email
            </span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {!isLogin && (
              <div className="border border-zinc-300 focus-within:border-[#002FA7] flex items-center">
                <span className="px-3 text-zinc-400">
                  <User className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="flex-1 h-12 outline-none text-sm pr-3 bg-transparent"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div className="border border-zinc-300 focus-within:border-[#002FA7] flex items-center">
              <span className="px-3 text-zinc-400">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 h-12 outline-none text-sm pr-3 bg-transparent"
                data-testid="auth-email-input"
                autoComplete="email"
              />
            </div>
            <div className="border border-zinc-300 focus-within:border-[#002FA7] flex items-center">
              <span className="px-3 text-zinc-400">
                <Lock className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 6 chars)"
                className="flex-1 h-12 outline-none text-sm pr-1 bg-transparent"
                data-testid="auth-password-input"
                autoComplete={isLogin ? "current-password" : "new-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="px-3 text-zinc-400 hover:text-zinc-900 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="auth-password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>

            {isLogin && (
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-[#002FA7] underline-offset-4 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            )}

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full h-12 bg-zinc-900 hover:bg-[#002FA7] disabled:opacity-50 text-white text-sm font-medium uppercase tracking-[0.12em] transition-colors"
              data-testid="auth-submit"
            >
              {busy ? "..." : isLogin ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-zinc-500 mt-6 text-center">
            {isLogin ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => {
                setMode(isLogin ? "register" : "login");
                setError("");
              }}
              className="text-zinc-900 font-medium underline-offset-4 hover:underline"
              data-testid="auth-toggle-mode"
            >
              {isLogin ? "Create an account" : "Sign in instead"}
            </button>
          </p>
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-6 text-center">
        <a
          href="https://charlottebryan.com.au/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#002FA7] transition-colors"
        >
          A <span className="font-semibold text-zinc-900">Charlotte Bryan</span> app ↗
        </a>
      </footer>
    </div>
  );
}
